import UploadDocumentsIcon from '@components/Icons/UploadDocumentsIcon';
import { Z_INDEX } from '@constants/z-index';
import { useModalStore } from '@stores/ModalStore';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import css from './DragDropOverlay.module.scss';

const EXIT_ANIMATION_MS = 100;

interface DragDropOverlayProps {
  isDragging: boolean;
}

/**
 * Full-viewport wrapper so the overlay is never clipped by parent overflow/contain.
 * Uses high z-index to ensure it appears above modals when drag-drop is allowed.
 */
const FULL_VIEWPORT_WRAPPER_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  minWidth: '100vw',
  minHeight: '100dvh',
  overflow: 'visible',
  zIndex: Z_INDEX.dragDropOverlay,
  pointerEvents: 'none',
};

export const DragDropOverlay = observer(({ isDragging }: DragDropOverlayProps): ReactNode => {
  const { allowDragDropOverlay, shouldDragDropOverlayBeTransparent } = useModalStore();
  const [isExiting, setIsExiting] = useState(false);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prevDraggingRef = useRef(false);

  const effectiveIsDragging = isDragging && allowDragDropOverlay;
  const showOverlay = effectiveIsDragging || isExiting;

  // useLayoutEffect runs synchronously before paint, preventing the flash where
  // showOverlay becomes false before isExiting becomes true
  useLayoutEffect(() => {
    if (effectiveIsDragging) {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      setIsExiting(false);
      prevDraggingRef.current = true;
      return;
    }
    // effectiveIsDragging just became false and we were showing → start exit animation
    if (prevDraggingRef.current) {
      prevDraggingRef.current = false;
      setIsExiting(true);
      exitTimeoutRef.current = setTimeout(() => {
        setIsExiting(false);
        exitTimeoutRef.current = null;
      }, EXIT_ANIMATION_MS);
    }
    return () => {
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
    };
  }, [effectiveIsDragging]);

  if (!showOverlay) return null;

  return createPortal(
    <div style={FULL_VIEWPORT_WRAPPER_STYLE}>
      <div
        className={classNames(
          css.overlay,
          isExiting && css.isExiting,
          shouldDragDropOverlayBeTransparent && css.isInvisible,
        )}
      >
        <div className={css.blurLayer} aria-hidden />
        <div className={css.tintLayer} aria-hidden />
        <div className={css.prompt}>
          <UploadDocumentsIcon />
          <span className={css.promptText}>Release to upload media.</span>
        </div>
      </div>
    </div>,
    document.body,
  ) as ReactNode;
});
